import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCustomSizes, createCustomSize, deleteCustomSize } from "@/integrations/supabase/custom-sizes";
import { CLOTHING_SIZES } from "@/constants/sizes";
import { logger } from "@/utils/logger";

export interface ClothingSize {
  value: string;
  label: string;
  isCustom?: boolean;
  id?: string; // ID do tamanho customizado no banco
}

/**
 * Hook para carregar e gerenciar tamanhos de roupas (padrão + customizados)
 * Combina os tamanhos padrão com os tamanhos customizados da empresa
 */
export function useClothingSizes(empresaId?: string) {
  const queryClient = useQueryClient();
  const [allSizes, setAllSizes] = useState<ClothingSize[]>([]);
  const [isLoadingSizes, setIsLoadingSizes] = useState(false);

  // Query para buscar tamanhos customizados
  const { data: customSizesData = [], refetch: refetchCustomSizes } = useQuery({
    queryKey: ["custom-sizes", empresaId],
    queryFn: () => (empresaId ? getCustomSizes(empresaId) : Promise.resolve([])),
    enabled: !!empresaId,
  });

  // Memoizar customSizes para evitar render loop
  const customSizes = useMemo(() => customSizesData, [customSizesData?.length]);

  // Combinar tamanhos padrão com customizados
  useEffect(() => {
    if (!empresaId) {
      setAllSizes(CLOTHING_SIZES as ClothingSize[]);
      return;
    }

    const combinedSizes: ClothingSize[] = [
      // Adicionar tamanhos padrão
      ...CLOTHING_SIZES.map((size) => ({
        ...size,
        isCustom: false,
      })),
      // Adicionar tamanhos customizados
      ...customSizes.map((custom) => ({
        value: custom.value,
        label: custom.label,
        isCustom: true,
        id: custom.id,
      })),
    ];

    // Remover duplicatas (se um customizado tiver o mesmo value que um padrão)
    const uniqueSizes = Array.from(
      new Map(combinedSizes.map((size) => [size.value, size])).values()
    );

    // Ordenar: padrão primeiro, depois customizados
    const sorted = [
      ...uniqueSizes.filter((s) => !s.isCustom),
      ...uniqueSizes.filter((s) => s.isCustom),
    ];

    setAllSizes(sorted);
  }, [empresaId, customSizes]);

  // Função para adicionar novo tamanho
  const addSize = useCallback(
    async (label: string, value: string, userId?: string) => {
      if (!empresaId) {
        logger.error("Empresa ID é necessário para adicionar tamanho customizado");
        return { ok: false, error: "Empresa não identificada" };
      }

      setIsLoadingSizes(true);
      try {
        const result = await createCustomSize(empresaId, label, value, userId);

        if (result.ok) {
          // Refazer a query para atualizar os tamanhos
          await refetchCustomSizes();
          // Invalidar cache
          queryClient.invalidateQueries({ queryKey: ["custom-sizes", empresaId] });
          logger.debug(`✅ Tamanho "${label}" adicionado com sucesso`);
        }

        return result;
      } finally {
        setIsLoadingSizes(false);
      }
    },
    [empresaId, refetchCustomSizes, queryClient]
  );

  // Função para deletar um tamanho
  const removeSize = useCallback(
    async (sizeId: string) => {
      setIsLoadingSizes(true);
      try {
        const result = await deleteCustomSize(sizeId);

        if (result.ok) {
          // Refazer a query para atualizar os tamanhos
          await refetchCustomSizes();
          // Invalidar cache
          queryClient.invalidateQueries({ queryKey: ["custom-sizes", empresaId] });
          logger.debug(`✅ Tamanho deletado com sucesso`);
        }

        return result;
      } finally {
        setIsLoadingSizes(false);
      }
    },
    [empresaId, refetchCustomSizes, queryClient]
  );

  return {
    sizes: allSizes,
    isLoading: isLoadingSizes,
    addSize,
    removeSize,
    refetch: refetchCustomSizes,
  };
}
